import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  FormContainer,
  LaurenQuoteMobile,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  RadioInput,
  StringInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { messages } from "~/utils/validations";

type Values = {
  journeyLooksLike: string;
  journeyOtherDetails: string;
};

const validationSchema = Yup.object({
  journeyLooksLike: Yup.string().required(messages.required),
  journeyOtherDetails: Yup.string().test(
    "journeyLooksLike",
    'This field is required when "Other" is selected.',
    function (value) {
      const { journeyLooksLike } = this.parent;
      return journeyLooksLike === "Other" ? Boolean(value) : true;
    },
  ),
});

const values: string[] = [
  "Timed intercourse/at home insemination",
  "Trying IUI",
  "Trying IVF",
  "Pursuing surrogacy",
  "Pursuing egg donor",
  "Pursuing sperm donor",
  "Pursuing fertility preservation",
  "Other",
];

export const JourneyLooksLikePage: FC = () => {
  const { t } = useTranslation();
  const { journeyLooksLike, journeyOtherDetails } = useOnboarding();

  // This step is necessary because this used to be multi-select and is stored as an Array on the backend.
  // We convert the string value submitted on this page to an Array before persisting in the Onboarding::Updater
  const selectedJourney = journeyLooksLike?.[0] || "";

  const { onSubmit } = useSubmitOnboardingQuestion();

  const initialValues: Values = {
    journeyLooksLike: selectedJourney || "",
    journeyOtherDetails: journeyOtherDetails || "",
  };

  const formik = useFormik({
    initialValues,
    onSubmit: (values, formikHelpers) => {
      // Convert the single string value to an array as expected by the backend
      const transformedValues = {
        ...values,
        journeyLooksLike: values.journeyLooksLike
          ? [values.journeyLooksLike]
          : [],
      };
      return onSubmit(transformedValues, formikHelpers);
    },
    validationSchema,
    validateOnMount: true,
  });

  const isAutoAdvance = (option: string) => option !== "Other";
  const { values: formikValues, setFieldValue } = formik;

  useEffect(() => {
    if (
      formikValues?.journeyLooksLike !== "Other" &&
      formikValues?.journeyOtherDetails !== ""
    ) {
      setFieldValue("journeyOtherDetails", "");
    }
  }, [
    formikValues.journeyLooksLike,
    formikValues.journeyOtherDetails,
    setFieldValue,
  ]);

  const showAdditionalQuestion = formikValues?.journeyLooksLike === "Other";

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            comment={t("onboarding.journeyLooksLikePage.comment")}
            description={t("onboarding.journeyLooksLikePage.description")}
            question={t("onboarding.journeyLooksLikePage.question")}
          />
          <LaurenQuoteMobile>
            {t("onboarding.journeyLooksLikePage.comment")}
          </LaurenQuoteMobile>
          <RadioInput
            autoAdvance={isAutoAdvance}
            id="journeyLooksLike"
            values={values}
            defaultValue={selectedJourney}
          />
        </QuestionContainer>
        {showAdditionalQuestion && (
          <QuestionContainer my={5}>
            <Prompt
              question={t(
                "onboarding.journeyLooksLikePage.additionalQuestion.question",
              )}
            />
            <StringInput
              id="journeyOtherDetails"
              placeholder={t(
                "onboarding.journeyLooksLikePage.additionalQuestion.placeholder",
              )}
            />
          </QuestionContainer>
        )}
        <NextButton />
      </FormContainer>
    </QuestionPageContainer>
  );
};
